/**
 * Main Command Registry
 * 
 * Tüm komutları birleştiren merkezi registry
 * Utility functions ile command arama
 */

import { navigationCommands } from './navigation-commands';
import { modelCommands } from './model-commands';
import { gamesCommands } from './games-commands';
import type { SlashCommand } from '../types/command-types';

/**
 * Command Registry
 * 
 * Tüm slash command'lar burada tanımlı
 */
export const commandRegistry: SlashCommand[] = [
  ...navigationCommands,
  ...modelCommands,
  ...gamesCommands,
  // Gelecekte: ...actionCommands
];

/**
 * Find Command by Trigger
 * 
 * Trigger string'ine göre command bulur (recursive)
 */
export const findCommandByTrigger = (
  trigger: string,
  commands: SlashCommand[] = commandRegistry
): SlashCommand | null => {
  for (const command of commands) {
    if (command.trigger === trigger) {
      return command;
    }
    
    // Check children recursively
    if (command.children) {
      const found = findCommandByTrigger(trigger, command.children);
      if (found) return found;
    }
  }
  
  return null;
};

/**
 * Find Command by ID
 * 
 * ID'ye göre command bulur (recursive)
 */
export const findCommandById = (
  id: string,
  commands: SlashCommand[] = commandRegistry
): SlashCommand | null => {
  for (const command of commands) {
    if (command.id === id) {
      return command;
    }
    
    // Check children recursively
    if (command.children) {
      const found = findCommandById(id, command.children);
      if (found) return found;
    }
  }
  
  return null;
};

/**
 * Find Command by Path
 * 
 * Path array'ine göre command bulur
 * Example: ["/model", "/model/llm"] → model-llm command
 */
export const findCommandByPath = (
  path: string[]
): SlashCommand | null => {
  if (path.length === 0) return null;
  
  const lastPath = path[path.length - 1];
  return findCommandByTrigger(lastPath);
};

/**
 * Get All Commands (Flat)
 * 
 * Tüm komutları flat list olarak döner (children dahil)
 */
export const getAllCommandsFlat = (
  commands: SlashCommand[] = commandRegistry
): SlashCommand[] => {
  const flatList: SlashCommand[] = [];
  
  for (const command of commands) {
    flatList.push(command);
    
    if (command.children) {
      flatList.push(...getAllCommandsFlat(command.children));
    }
  }
  
  return flatList;
};

/**
 * Get All Commands (Root only)
 * 
 * Sadece root level command'ları döner
 */
export const getAllCommands = (): SlashCommand[] => commandRegistry;

/**
 * Search Commands
 * 
 * Command'ları search query'ye göre filtreler
 * Label, description, trigger'da arar
 */
export const searchCommands = (
  query: string,
  commands: SlashCommand[] = commandRegistry
): SlashCommand[] => {
  const lowerQuery = query.toLowerCase();
  const results: SlashCommand[] = [];
  
  for (const command of commands) {
    const matchesLabel = command.label.toLowerCase().includes(lowerQuery);
    const matchesDescription = command.description?.toLowerCase().includes(lowerQuery);
    const matchesTrigger = command.trigger.toLowerCase().includes(lowerQuery);
    
    if (matchesLabel || matchesDescription || matchesTrigger) {
      results.push(command);
    }
    
    // Search in children recursively
    if (command.children) {
      const childResults = searchCommands(query, command.children);
      results.push(...childResults);
    }
  }
  
  return results;
};

